import React, { useEffect, useRef } from 'react';
import { X, AlertCircle, AlertTriangle, CheckCircle, Info } from 'lucide-react';
import { Button } from './Button';
import { focusRing } from '@shared/utils/statusTone';
import { useTranslation } from 'react-i18next';

export type DialogVariant = 'info' | 'success' | 'warning' | 'error' | 'confirm' | 'danger';

export interface DialogButton {
  label: string;
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';
  onClick?: () => void;
  autoFocus?: boolean;
}

export interface DialogOptions {
  title: string;
  message: string;
  variant?: DialogVariant;
  buttons?: DialogButton[];
  onClose?: () => void;
  closeOnOverlayClick?: boolean;
  closeOnEscape?: boolean;
  icon?: React.ReactNode;
}

interface DialogProps extends DialogOptions {
  isOpen: boolean;
}

const variantIcons: Record<DialogVariant, React.ReactNode> = {
  info: <Info className="w-6 h-6 text-state-info-light dark:text-state-info-dark" />,
  success: <CheckCircle className="w-6 h-6 text-state-ok-light dark:text-state-ok-dark" />,
  warning: <AlertTriangle className="w-6 h-6 text-state-warn-light dark:text-state-warn-dark" />,
  error: <AlertCircle className="w-6 h-6 text-state-danger-light dark:text-state-danger-dark" />,
  danger: <AlertCircle className="w-6 h-6 text-state-danger-light dark:text-state-danger-dark" />,
  confirm: <AlertCircle className="w-6 h-6 text-brand-600 dark:text-brand-400" />,
};

const variantStyles: Record<DialogVariant, string> = {
  info: 'border-l-4 border-l-state-info-light dark:border-l-state-info-dark',
  success: 'border-l-4 border-l-state-ok-light dark:border-l-state-ok-dark',
  warning: 'border-l-4 border-l-state-warn-light dark:border-l-state-warn-dark',
  error: 'border-l-4 border-l-state-danger-light dark:border-l-state-danger-dark',
  danger: 'border-l-4 border-l-state-danger-light dark:border-l-state-danger-dark',
  confirm: 'border-l-4 border-l-brand-600 dark:border-l-brand-400',
};

export const Dialog: React.FC<DialogProps> = ({
  isOpen,
  title,
  message,
  variant = 'info',
  buttons = [],
  onClose,
  closeOnOverlayClick = true,
  closeOnEscape = true,
  icon,
}) => {
  const { t } = useTranslation('common');
  const dialogRef = useRef<HTMLDivElement>(null);
  const openerRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!isOpen) return;

    openerRef.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const focusFrame = requestAnimationFrame(() => {
      const initialFocus =
        dialogRef.current?.querySelector<HTMLElement>('[data-dialog-autofocus="true"]') ??
        dialogRef.current?.querySelector<HTMLElement>('button:not([disabled])');
      initialFocus?.focus();
    });

    return () => {
      cancelAnimationFrame(focusFrame);
      document.body.style.overflow = previousBodyOverflow;
      openerRef.current?.focus();
      openerRef.current = null;
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && closeOnOverlayClick && onClose) {
      onClose();
    }
  };

  const handleDialogKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape' && closeOnEscape && onClose) {
      event.preventDefault();
      onClose();
      return;
    }

    if (event.key !== 'Tab') return;

    const focusable = Array.from(
      event.currentTarget.querySelectorAll<HTMLElement>(
        'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [href], [tabindex]:not([tabindex="-1"])'
      )
    ).filter((element) => element.offsetParent !== null);
    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (focusable.length === 0) {
      event.preventDefault();
      dialogRef.current?.focus();
    } else if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const defaultButtons: DialogButton[] =
    buttons.length > 0
      ? buttons
      : [
          {
            label: t('actions.ok'),
            variant: 'primary',
            onClick: onClose,
            autoFocus: true,
          },
        ];

  return (
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center p-4 animate-in fade-in duration-200"
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="dialog-title"
      ref={dialogRef}
      onKeyDown={handleDialogKeyDown}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />

      {/* Dialog Content */}
      <div
        className={`
          relative w-full max-w-md bg-surface-card-light dark:bg-surface-card-dark rounded-xl shadow-2xl
          transform transition-all animate-in zoom-in-95 duration-200
          ${variantStyles[variant]}
        `}
      >
        {/* Close Button */}
        {onClose && (
          <button
            onClick={onClose}
            className={`absolute top-3 right-3 min-h-11 min-w-11 p-2 rounded-lg text-text-muted-light dark:text-text-muted-dark hover:text-slate-900 dark:hover:text-white hover:bg-surface-2-light dark:hover:bg-surface-2-dark transition-colors ${focusRing}`}
            aria-label={t('actions.close')}
          >
            <X className="w-5 h-5" />
          </button>
        )}

        {/* Content */}
        <div className="p-6">
          {/* Header */}
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 mt-0.5">{icon || variantIcons[variant]}</div>
            <div className="flex-1 min-w-0">
              <h3
                id="dialog-title"
                className="text-lg font-semibold text-slate-900 dark:text-white mb-2"
              >
                {title}
              </h3>
              <div className="text-slate-600 dark:text-slate-300 text-sm leading-relaxed whitespace-pre-wrap">
                {message}
              </div>
            </div>
          </div>

          {/* Buttons */}
          <div className="mt-6 flex flex-wrap justify-end gap-3">
            {defaultButtons.map((button, index) => (
              <Button
                key={index}
                variant={button.variant || 'primary'}
                onClick={() => {
                  button.onClick?.();
                }}
                data-dialog-autofocus={button.autoFocus ? 'true' : undefined}
              >
                {button.label}
              </Button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dialog;
